import { tr, usePartnerTranslation } from "../../lib/language";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Paperclip, Send, LifeBuoy } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { parseApiError } from "../../lib/api";

const statuses = new Set(["resolved", "closed"]);
const read = (obj, snake, camel = snake) => obj?.[snake] ?? obj?.[camel];

function toFormData(values, files) {
  const form = new FormData();
  Object.entries(values).forEach(([key, value]) => form.append(key, value));
  const attachment = Array.from(files || [])[0];
  if (attachment) form.append("attachments", attachment);
  return form;
}

export default function SupportTicketsPage() {
  usePartnerTranslation();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState(() => searchParams.get("ticket"));
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "general", priority: "medium", message: "" });
  const [files, setFiles] = useState([]);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);

  const selected = useMemo(
    () => detail || tickets.find((ticket) => read(ticket, "id") === selectedId),
    [detail, selectedId, tickets],
  );
  const locked = selected ? statuses.has(read(selected, "status")) : false;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets", { params: { per_page: 50 } });
      const items = data?.items || [];
      setTickets(items);
      if (!selectedId && items[0]) setSelectedId(read(items[0], "id"));
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    api.get(`/support/tickets/${selectedId}`)
      .then(({ data }) => setDetail(data))
      .catch((err) => toast.error(parseApiError(err).message));
  }, [selectedId]);

  const createTicket = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/support/tickets", toFormData(form, files), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(tr("Ticket created"));
      setForm({ subject: "", category: "general", priority: "medium", message: "" });
      setFiles([]);
      setSelectedId(read(data, "id"));
      await load();
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selectedId || locked) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/support/tickets/${selectedId}/reply`, toFormData({ message: reply }, replyFiles), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDetail(data);
      setReply("");
      setReplyFiles([]);
      toast.success(tr("Reply sent"));
      await load();
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="partner-support-page">
      <div className="mb-6 flex items-center gap-3">
        <LifeBuoy className="h-6 w-6 text-[#FF5757]" />
        <div>
          <h1 className="text-2xl font-semibold text-[#0F1115]">{tr("Support tickets")}</h1>
          <p className="text-sm text-[#6B7280]">{tr("Create tickets and continue conversations with Voocly support.")}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <section className="space-y-4">
          <form onSubmit={createTicket} className="rounded-lg border border-[#EAECEF] bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-[#0F1115]">{tr("New ticket")}</h2>
            <div className="space-y-3">
              <input required value={form.subject} onChange={(e) => setForm((v) => ({ ...v, subject: e.target.value }))} placeholder={tr("Subject")} className="w-full rounded-md border border-[#DDE1E6] px-3 py-2 text-sm outline-none focus:border-[#2476FF]" />
              <select value={form.category} onChange={(e) => setForm((v) => ({ ...v, category: e.target.value }))} className="w-full rounded-md border border-[#DDE1E6] px-3 py-2 text-sm">
                <option value="general">{tr("General")}</option>
                <option value="bookings">{tr("Bookings")}</option>
                <option value="payments">{tr("Payments")}</option>
                <option value="product_setup">{tr("Product setup")}</option>
                <option value="technical_issue">{tr("Technical issue")}</option>
              </select>
              <select value={form.priority} onChange={(e) => setForm((v) => ({ ...v, priority: e.target.value }))} className="w-full rounded-md border border-[#DDE1E6] px-3 py-2 text-sm">
                <option value="low">{tr("Low")}</option>
                <option value="medium">{tr("Medium")}</option>
                <option value="high">{tr("High")}</option>
                <option value="urgent">{tr("Urgent")}</option>
              </select>
              <textarea required value={form.message} onChange={(e) => setForm((v) => ({ ...v, message: e.target.value }))} placeholder={tr("Message")} rows={4} className="w-full rounded-md border border-[#DDE1E6] px-3 py-2 text-sm outline-none focus:border-[#2476FF]" />
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-[#DDE1E6] px-3 py-2 text-sm text-[#6B7280]">
                <Paperclip className="h-4 w-4" />
                <span>{files[0]?.name || tr("Attach one PDF, JPG or PNG")}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setFiles(e.target.files?.[0] ? [e.target.files[0]] : [])} />
              </label>
              <button disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FF5757] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{tr("Create ticket")}</button>
            </div>
          </form>

          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-[#EAECEF] bg-white shadow-sm">
            {loading ? (
              <div className="p-6 text-center text-sm text-[#6B7280]">{tr("Loading...")}</div>
            ) : tickets.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6B7280]">{tr("No tickets yet.")}</div>
            ) : tickets.map((ticket) => (
              <button key={read(ticket, "id")} onClick={() => setSelectedId(read(ticket, "id"))} className={`block w-full border-b border-[#F0F2F4] p-4 text-start last:border-b-0 ${read(ticket, "id") === selectedId ? "bg-[#FFF4F4]" : "hover:bg-[#FAFAFB]"}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-[#0F1115]">{read(ticket, "subject")}</span>
                  <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs text-[#4B5563]">{read(ticket, "status")}</span>
                </div>
                <p className="mt-1 text-xs text-[#6B7280]">{read(ticket, "reference")}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-[#EAECEF] bg-white p-5 shadow-sm">
          {!selected ? (
            <div className="py-16 text-center text-sm text-[#6B7280]">{tr("Select a ticket.")}</div>
          ) : (
            <>
              <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-[#EAECEF] pb-4">
                <div>
                  <h2 className="text-lg font-semibold text-[#0F1115]">{read(selected, "subject")}</h2>
                  <p className="text-sm text-[#6B7280]">{read(selected, "reference")} · {read(selected, "category")}</p>
                </div>
                <span className="rounded-full bg-[#F3F4F6] px-3 py-1 text-xs font-semibold text-[#374151]">{read(selected, "status")}</span>
              </div>
              <div className="space-y-4">
                {(read(selected, "messages") || []).map((message) => (
                  <div key={read(message, "id")} className="rounded-lg border border-[#EAECEF] p-4">
                    <div className="mb-2 flex justify-between gap-3 text-xs text-[#6B7280]">
                      <span>{read(message, "author_name", "authorName")} · {read(message, "sender_type", "senderType")}</span>
                      <span>{new Date(read(message, "created_at", "createdAt")).toLocaleString()}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-[#111827]">{read(message, "body")}</p>
                    {(read(message, "attachments") || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {read(message, "attachments").map((file) => (
                          <a key={read(file, "id")} href={read(file, "url")} className="text-xs font-semibold text-[#2476FF]" target="_blank" rel="noreferrer">{read(file, "original_filename", "originalFilename")}</a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={sendReply} className="mt-5 border-t border-[#EAECEF] pt-4">
                {locked ? (
                  <p className="text-sm text-[#6B7280]">{tr("This ticket is ")}{read(selected, "status")}{tr("; replies are closed.")}</p>
                ) : (
                  <div className="space-y-3">
                    <textarea required value={reply} onChange={(e) => setReply(e.target.value)} placeholder={tr("Write a reply")} rows={4} className="w-full rounded-md border border-[#DDE1E6] px-3 py-2 text-sm outline-none focus:border-[#2476FF]" />
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <label className="flex cursor-pointer items-center gap-2 text-sm text-[#6B7280]">
                        <Paperclip className="h-4 w-4" />
                        <span>{replyFiles[0]?.name || tr("Attach one PDF, JPG or PNG")}</span>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setReplyFiles(e.target.files?.[0] ? [e.target.files[0]] : [])} />
                      </label>
                      <button disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#0F1115] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                        <Send className="h-4 w-4" />{tr(" Send reply")}</button>
                    </div>
                  </div>
                )}
              </form>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
